import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
// hooks
import useGetFetch from "../../../hooks/useGetFetch";
// global
import { dbDataStore, messageAndErrorStore } from "../../../store/Store";
// components
import Loading from "../../../components/Loading";
import ErrorDisplay from "../../../components/ErrorDisplay";
import InputField from "../../../formComponents/InputField";
import InvoiceDetailFilter from "../../vendor-invoices/InvoiceDetailFilter";
import DeliveredDate from "../../../components/DeliveredDate";
import MessageCard from "../../../components/MessageCard";

// assets
import AddAdjustment from "../AddAdjustment";
import DeleteAdjustment from "../DeleteAdjustment";
import AdjustmentDetails from "../../../invoiceComponents/AdjustmentDetails";

export default function AccountVendorInvoiceDetails() {
  //global
  const projectData = dbDataStore((data) => data.projectData);
  const messageTrigger = messageAndErrorStore((data) => data.messageTrigger);
  const pageError = messageAndErrorStore((messages) => messages.error);
  const successMessage = messageAndErrorStore(
    (messages) => messages.successMessage,
  );
  // local
  const { invId } = useParams("invId");
  const [invoiceDetails, setInvoiceDetails] = useState();
  const [mismatchValue, setMismatchValue] = useState(2.0);
  const [visibleLoads, setVisibleLoads] = useState([]);
  const [canceled, setCanceled] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loads, setLoads] = useState([]);
  const [adjustments, setAdjustments] = useState();
  const [editAdjustmentTrigger, setEditAdjustmentTrigger] = useState(false);
  const [deleteAdjustmentTrigger, setDeleteAdjustmentTrigger] = useState(false);

  const { isPending, error } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/account-vendor-invoices/${invId}`,
    setInvoiceDetails,
  );

  const { isPending: adjustmentsPending, error: adjustmentsError } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/lc/invoice-adjustment/${invId}?is_vendor=true&is_received=false&project_id=0`,
      setAdjustments,
    );

  
  // freight load filtering
  useEffect(() => {
    if (invoiceDetails && invoiceDetails.vendor_invoice_loads) {
      const canceledLoads = [];
      const regular = [];
      invoiceDetails.vendor_invoice_loads.forEach((l) => {
        if (invoiceDetails.project_loads) {
          var projectLoad = invoiceDetails.project_loads.filter(
            (x) => x.id === l.load_id,
          );
          if (projectLoad.length !== 0 && projectLoad[0].is_canceled) {
            canceledLoads.push(l);
          } else {
            regular.push(l);
          }
        }
      });
      if (regular.length > 0) {
        setLoads(regular);
        setVisibleLoads(regular);
      } else {
        setLoads(invoiceDetails.vendor_invoice_loads);
        setVisibleLoads(invoiceDetails.vendor_invoice_loads);
      }
      setCanceled(canceledLoads);
    }
  }, [invoiceDetails, setCanceled, setLoads, setVisibleLoads]);

  // asset load filtering
  useEffect(() => {
    if (
      invoiceDetails &&
      invoiceDetails.vendor_invoice_loads &&
      invoiceDetails.project_asset_loads
    ) {
      const regular = [];
      invoiceDetails.vendor_invoice_loads.forEach((l) => {
        var projectLoad = invoiceDetails.project_asset_loads.filter(
          (x) => x.id === l.load_id,
        );
        if (projectLoad.length !== 0) {
          regular.push(l);
        }
      });
      setLoads(regular);
      setVisibleLoads(regular);
    }
  }, [invoiceDetails, setLoads, setVisibleLoads]);

  const calcTotals = (invLoad) => {
    var loadTotals = {
      weight: 0,
      weight_diff: 0,
      total_cost: invLoad.freight_cost + invLoad.fsc_cost,
      calc_cost: 0,
      cost_diff: 0,
    };
    var load;
    if (
      !invoiceDetails.vendor_invoice.is_asset &&
      invoiceDetails.project_loads
    ) {
      load = invoiceDetails.project_loads.filter(
        (x) => x.id === invLoad.load_id,
      );
      if (load.length > 0) {
        loadTotals.weight = load[0].weight_tons;
        loadTotals.weight_diff = invLoad.weight_tons - load[0].weight_tons;
        loadTotals.calc_cost = load[0].freight_cost + load[0].fsc_cost;
        loadTotals.cost_diff = loadTotals.total_cost - loadTotals.calc_cost;
      }
    } else if (
      invoiceDetails.vendor_invoice.is_asset &&
      invoiceDetails.project_asset_loads
    ) {
      load = invoiceDetails.project_asset_loads.filter(
        (x) => x.id === invLoad.load_id,
      );
      if (load.length > 0) {
        loadTotals.weight = load[0].weight_tons;
        loadTotals.weight_diff = invLoad.weight_tons - load[0].weight_tons;
        loadTotals.calc_cost = load[0].freight_cost + load[0].fsc_cost;
        loadTotals.cost_diff = loadTotals.total_cost - loadTotals.calc_cost;
      }
    } else {
    }
    return loadTotals;
  };

  const handleMismatch = (costDiff) => {
    if (Math.abs(costDiff) > mismatchValue) {
      return "Yes";
    } else {
      return "";
    }
  };

  if (isPending || adjustmentsPending) {
    return <Loading />;
  } else if (error || adjustmentsError) {
    return <ErrorDisplay message={"Could not load data"} code={500} />;
  } else if (invoiceDetails) {
    return (
      <div className="container-fluid">
        {messageTrigger && (
          <MessageCard errorMessage={pageError} successMessage={successMessage} />
        )}
        <div className="d-flex justify-content-between project-header">
          <h2>Invoice : {invoiceDetails.vendor_invoice.invoice_name}</h2>
          <InputField
            name={"mismatch-value"}
            title={"Mismatch Threshold $"}
            value={mismatchValue}
            type={"number"}
            increment={0.01}
            onChange={(e) => setMismatchValue(e.target.value)}
            required
          />
        </div>
        {adjustments &&
          adjustments.invoice_adjustment &&
          adjustments.adjustment_fields &&
          !adjustmentsPending && (
            <AdjustmentDetails
              adjustments={adjustments}
              setEditAdjustmentTrigger={setEditAdjustmentTrigger}
              setDeleteAdjustmentTrigger={setDeleteAdjustmentTrigger}
            />
          )}
        {canceled.length !== 0 && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Canceled Loads</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Job</th>
                    <th>Delivered</th>
                    <th>Driver</th>
                    {!invoiceDetails.vendor_invoice.is_asset && <th>Ticket</th>}
                    <th>Weight</th>
                    <th>Calc Weight</th>
                    <th>Weight Diff</th>
                    {!invoiceDetails.vendor_invoice.is_asset && (
                      <th>Product</th>
                    )}
                    <th>Freight Cost</th>
                    <th>FSC Cost</th>
                    <th>Total Cost</th>
                    <th>Calc Cost</th>
                    <th>Cost Diff</th>
                    <th>Mismatch</th>
                  </tr>
                </thead>
                <tbody>
                  {canceled.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>
                        {
                          projectData.filter((x) => x.id === l.project_id)[0]
                            .job_name
                        }
                      </td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.driver_name}</td>
                      {!invoiceDetails.vendor_invoice.is_asset && (
                        <td>{l.ticket_number}</td>
                      )}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? calcTotals(l).weight.toFixed(2)
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? calcTotals(l).weight_diff.toFixed(2)
                          : "Closed"}
                      </td>
                      {!invoiceDetails.vendor_invoice.is_asset && (
                        <td>{l.product_name}</td>
                      )}
                      <td>${l.freight_cost.toFixed(2)}</td>
                      <td>${l.fsc_cost.toFixed(2)}</td>
                      <td>${calcTotals(l).total_cost.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? `$${calcTotals(l).calc_cost.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? `$${calcTotals(l).cost_diff.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? `${handleMismatch(calcTotals(l).cost_diff)}`
                          : "Closed"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {invoiceDetails.uninvoiced_loads && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Loads Not Invoiced</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Job</th>
                    <th>Delivered</th>
                    <th>Driver</th>
                    {!invoiceDetails.vendor_invoice.is_asset && <th>Ticket</th>}
                    <th>Weight</th>
                    {!invoiceDetails.vendor_invoice.is_asset && (
                      <th>Product</th>
                    )}
                    <th>Freight Cost</th>
                    <th>FSC Cost</th>
                    <th>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceDetails.uninvoiced_loads.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>
                        {
                          projectData.filter((x) => x.id === l.project_id)[0]
                            .job_name
                        }
                      </td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.driver_name}</td>
                      {!invoiceDetails.vendor_invoice.is_asset && (
                        <td>{l.ticket_number}</td>
                      )}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      {!invoiceDetails.vendor_invoice.is_asset && (
                        <td>{l.product_name}</td>
                      )}
                      <td>${l.freight_cost.toFixed(2)}</td>
                      <td>${l.fsc_cost.toFixed(2)}</td>
                      <td>${calcTotals(l).total_cost.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="d-flex justify-content-between project-header">
          {loads.length !== 0 && <h4>Loads</h4>}
        </div>
        {loads.length !== 0 && (
          <div className="row g-2">
            <InvoiceDetailFilter
              isCustomer={true}
              customerFilter={filter}
              setCustomerfilter={setFilter}
            />
          </div>
        )}
        <div className="load-display-list">
          {loads.length !== 0 && (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Load</th>
                  <th>Job</th>
                  <th>Delivered</th>
                  <th>Driver</th>
                  {!invoiceDetails.vendor_invoice.is_asset && <th>Ticket</th>}
                  <th>Weight</th>
                  <th>Calc Weight</th>
                  <th>Weight Diff</th>
                  {!invoiceDetails.vendor_invoice.is_asset && <th>Product</th>}
                  <th>Freight Cost</th>
                  <th>FSC Cost</th>
                  <th>Total Cost</th>
                  <th>Calc Cost</th>
                  <th>Cost Diff</th>
                  <th>Mismatch</th>
                </tr>
              </thead>
              <tbody>
                {visibleLoads.map((l, idx) =>
                  filter !== "cost mismatch" ? (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>
                        {
                          projectData.filter((x) => x.id === l.project_id)[0]
                            .job_name
                        }
                      </td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.driver_name}</td>
                      {!invoiceDetails.vendor_invoice.is_asset && (
                        <td>{l.ticket_number}</td>
                      )}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? calcTotals(l).weight.toFixed(2)
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? calcTotals(l).weight_diff.toFixed(2)
                          : "Closed"}
                      </td>
                      {!invoiceDetails.vendor_invoice.is_asset && (
                        <td>{l.product_name}</td>
                      )}
                      <td>${l.freight_cost.toFixed(2)}</td>
                      <td>${l.fsc_cost.toFixed(2)}</td>
                      <td>${calcTotals(l).total_cost.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? `$${calcTotals(l).calc_cost.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? `$${calcTotals(l).cost_diff.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.vendor_invoice.invoice_status_id !== 30
                          ? `${handleMismatch(calcTotals(l).cost_diff)}`
                          : "Closed"}
                      </td>
                    </tr>
                  ) : (
                    filter === "cost mismatch" &&
                    Math.abs(calcTotals(l).cost_diff) > mismatchValue && (
                      <tr key={idx}>
                        <td>{l.load_no}</td>
                        <td>
                          {
                            projectData.filter((x) => x.id === l.project_id)[0]
                              .job_name
                          }
                        </td>
                        <DeliveredDate d={l.delivered_date} />
                        <td>{l.driver_name}</td>
                        {!invoiceDetails.vendor_invoice.is_asset && (
                          <td>{l.ticket_number}</td>
                        )}
                        <td>{l.weight_tons.toFixed(2)}</td>
                        <td>
                          {invoiceDetails.vendor_invoice.invoice_status_id !==
                          30
                            ? calcTotals(l).weight.toFixed(2)
                            : "Closed"}
                        </td>
                        <td>
                          {invoiceDetails.vendor_invoice.invoice_status_id !==
                          30
                            ? calcTotals(l).weight_diff.toFixed(2)
                            : "Closed"}
                        </td>
                        {!invoiceDetails.vendor_invoice.is_asset && (
                          <td>{l.product_name}</td>
                        )}
                        <td>${l.freight_cost.toFixed(2)}</td>
                        <td>${l.fsc_cost.toFixed(2)}</td>
                        <td>${calcTotals(l).total_cost.toFixed(2)}</td>
                        <td>
                          {invoiceDetails.vendor_invoice.invoice_status_id !==
                          30
                            ? `$${calcTotals(l).calc_cost.toFixed(2)}`
                            : "Closed"}
                        </td>
                        <td>
                          {invoiceDetails.vendor_invoice.invoice_status_id !==
                          30
                            ? `$${calcTotals(l).cost_diff.toFixed(2)}`
                            : "Closed"}
                        </td>
                        <td>
                          {invoiceDetails.vendor_invoice.invoice_status_id !==
                          30
                            ? `${handleMismatch(calcTotals(l).cost_diff)}`
                            : "Closed"}
                        </td>
                      </tr>
                    )
                  ),
                )}
              </tbody>
            </table>
          )}
        </div>
        {editAdjustmentTrigger && adjustments && (
          <AddAdjustment
            inv={invoiceDetails.vendor_invoice}
            setTrigger={setEditAdjustmentTrigger}
            projectId={0}
            isVendor={true}
            id={adjustments.invoice_adjustment.id}
            editAdjustment={adjustments.invoice_adjustment}
            editAdjustmentFields={adjustments.adjustment_fields}
            setAdjustments={setAdjustments}
          />
        )}
        {deleteAdjustmentTrigger && (
          <DeleteAdjustment
            setTrigger={setDeleteAdjustmentTrigger}
            id={adjustments.invoice_adjustment.id}
            setAdjustments={setAdjustments}
            name={invoiceDetails.vendor_invoice.invoice_name}
          />
        )}
      </div>
    );
  }
}
